import { useState } from 'react';
import { makeAssistantToolUI, type ToolCallMessagePartProps } from '@assistant-ui/react';
import { Check, MessageCircleQuestion } from 'lucide-react';
import { DELIVERABLE_SURFACE, ToolRow } from '../toolRow';

interface AskOption {
  label: string;
  description: string;
}

interface AskQuestion {
  question: string;
  header: string;
  multiSelect?: boolean;
  options: AskOption[];
}

interface AskUserArgs {
  questions?: AskQuestion[];
  _thought?: string;
}

interface AskUserResult {
  answers?: Record<string, string>;
}

/** What one question's picks become in the result: labels joined with ", ", then the typed text. */
function answerFor(picked: ReadonlySet<string>, other: string, otherOn: boolean): string {
  const parts = [...picked];
  const typed = other.trim();
  if (otherOn && typed) parts.push(typed);
  return parts.join(', ');
}

const RECOMMENDED = /\s*\(recommended\)\s*$/i;

/** Radio dot or checkbox tick, in the accent colour when on. */
function Indicator({ multi, on }: { multi: boolean; on: boolean }) {
  return (
    <span
      aria-hidden
      className={`mt-[3px] flex h-3.5 w-3.5 shrink-0 items-center justify-center border transition-colors ${
        multi ? 'rounded-[3px]' : 'rounded-full'
      } ${on ? 'border-accent-500 bg-accent-500 text-white' : 'border-zinc-300 bg-white'}`}
    >
      {on && (multi ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <span className="h-1.5 w-1.5 rounded-full bg-white" />)}
    </span>
  );
}

const optionRow = (on: boolean) =>
  `flex w-full items-start gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 disabled:cursor-default ${
    on ? 'border-accent-200 bg-accent-50' : 'border-transparent hover:bg-zinc-50'
  }`;

/**
 * `ask_user` — the agent's questions, answered in place.
 *
 * The tool has no server side: the turn ends on the call, the user's pick is
 * added as the call's result, and `useChat` sends it back on its own
 * (`sendAutomaticallyWhen` in AiChatRuntime). Each question is one choice, or
 * several with `multiSelect`; the last row takes the user's own words. Typing
 * a new message instead leaves the questions unanswered, and the agent reads
 * that message as the reply.
 */
export function AskUserCard({ args, result, status, isError, addResult }: ToolCallMessagePartProps<AskUserArgs, AskUserResult>) {
  const questions: AskQuestion[] = Array.isArray(args?.questions) ? (args.questions as AskQuestion[]) : [];
  const [picked, setPicked] = useState<Record<number, Set<string>>>({});
  const [otherOn, setOtherOn] = useState<Record<number, boolean>>({});
  const [other, setOther] = useState<Record<number, string>>({});
  const [sent, setSent] = useState(false);

  const streaming = status.type === 'running' && result === undefined;
  const answers = result && typeof result === 'object' ? result.answers : undefined;
  const unanswered = !answers && (isError || result !== undefined);

  if (streaming) {
    return <ToolRow icon={<MessageCircleQuestion />} label="Preparing questions" running open={false} />;
  }

  if (answers || unanswered) {
    return (
      <div className="my-1">
        <ToolRow
          icon={<MessageCircleQuestion />}
          label={answers ? 'Answered' : 'Not answered'}
          meta={unanswered ? 'replied in chat' : undefined}
          open={false}
        />
        <dl className="space-y-0.5 pb-1 pl-[22px] text-meta">
          {questions.map((q) => (
            <div key={q.question} className="flex min-w-0 gap-2">
              <dt className="shrink-0 text-zinc-400">{q.header}</dt>
              <dd className={`min-w-0 ${answers?.[q.question] ? 'text-zinc-800' : 'text-zinc-400'}`}>
                {answers?.[q.question]?.replace(RECOMMENDED, '') || q.question}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  const answerOf = (i: number) => answerFor(picked[i] ?? new Set(), other[i] ?? '', !!otherOn[i]);
  const complete = questions.length > 0 && questions.every((_, i) => answerOf(i) !== '');

  const choose = (i: number, label: string, multi: boolean) => {
    setPicked((prev) => {
      const next = new Set(multi ? prev[i] : []);
      if (multi && next.has(label)) next.delete(label); else next.add(label);
      return { ...prev, [i]: next };
    });
    if (!multi) setOtherOn((prev) => ({ ...prev, [i]: false }));
  };
  const focusOther = (i: number, multi: boolean) => {
    setOtherOn((prev) => ({ ...prev, [i]: true }));
    if (!multi) setPicked((prev) => ({ ...prev, [i]: new Set() }));
  };
  const submit = () => {
    if (!complete || sent) return;
    setSent(true);
    addResult({ answers: Object.fromEntries(questions.map((q, i) => [q.question, answerOf(i)])) });
  };

  return (
    <div className={`${DELIVERABLE_SURFACE} divide-y divide-zinc-100`}>
      {questions.map((q, i) => {
        const multi = !!q.multiSelect;
        const chosen = picked[i] ?? new Set<string>();
        const ownOn = !!otherOn[i];
        return (
          <fieldset key={q.question} disabled={sent} aria-label={q.question} className="min-w-0 px-3 pb-2.5 pt-3">
            <div>
              <span className="block text-xs font-medium text-zinc-500">{q.header}</span>
              <span className="mt-0.5 block text-sm text-zinc-900">{q.question}</span>
              {multi && <span className="block text-xs text-zinc-400">Select all that apply</span>}
            </div>
            <div role={multi ? 'group' : 'radiogroup'} className="mt-2 space-y-0.5">
              {(q.options ?? []).map((o) => {
                const on = chosen.has(o.label);
                const recommended = RECOMMENDED.test(o.label);
                return (
                  <button
                    key={o.label}
                    type="button"
                    role={multi ? 'checkbox' : 'radio'}
                    aria-checked={on}
                    onClick={() => choose(i, o.label, multi)}
                    className={optionRow(on)}
                  >
                    <Indicator multi={multi} on={on} />
                    <span className="min-w-0">
                      <span className="block text-meta text-zinc-900">
                        {o.label.replace(RECOMMENDED, '')}
                        {recommended && <span className="ml-1.5 text-xs text-zinc-400">Recommended</span>}
                      </span>
                      {o.description && <span className="block text-xs leading-snug text-zinc-500">{o.description}</span>}
                    </span>
                  </button>
                );
              })}
              <label className={`${optionRow(ownOn)} cursor-text`}>
                <Indicator multi={multi} on={ownOn && !!other[i]?.trim()} />
                <input
                  value={other[i] ?? ''}
                  onFocus={() => focusOther(i, multi)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setOther((prev) => ({ ...prev, [i]: value }));
                    setOtherOn((prev) => ({ ...prev, [i]: true }));
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  placeholder="Other…"
                  className="min-w-0 flex-1 bg-transparent text-meta text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </label>
            </div>
          </fieldset>
        );
      })}
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <span className="text-xs text-zinc-400">Or reply in the message box</span>
        <button
          type="button"
          onClick={submit}
          disabled={!complete || sent}
          className="inline-flex h-7 items-center rounded-md bg-accent-500 px-3 text-xs font-medium text-white transition-colors hover:bg-accent-600 disabled:bg-zinc-100 disabled:text-zinc-400"
        >
          Submit
        </button>
      </div>
    </div>
  );
}

export const AskUserToolUI = makeAssistantToolUI<AskUserArgs, AskUserResult>({ toolName: 'ask_user', render: AskUserCard });
